// Quiz deelnemers & resultaten (web): ranking, answers per person and statistics per question.
import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../../components/Modal/Modal";
import { getQuizParticipants } from "../../../utils/api";
import "./QuizParticipants.css";

const fmtDate = (d) => (d ? new Date(d).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" }) : "-");
const tone = (pct) => (pct >= 80 ? "good" : pct >= 50 ? "mid" : "bad");

const toCsv = (data) => {
  const header = ["Plaats", "Naam", "E-mail", "Datum", "Score", "Totaal", "Percentage"];
  const lines = data.participants.map((p, i) => [i + 1, p.name, p.email, fmtDate(p.participatedOn), p.score, p.total, `${p.percentage}%`]);
  return [header, ...lines]
    .map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";"))
    .join("\n");
};

export default function QuizParticipants({ quiz, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("participants");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    getQuizParticipants(quiz._id)
      .then(setData)
      .catch((err) => setError(err.message || "Kon deelnemers niet laden"));
  }, [quiz._id]);

  const questionById = useMemo(() => new Map((data?.questions || []).map((q) => [String(q._id), q])), [data]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = data?.participants || [];
    return q ? list.filter((p) => p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)) : list;
  }, [data, search]);

  const downloadCsv = () => {
    const blob = new Blob([`﻿${toCsv(data)}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quiz-${(quiz.title || "resultaten").replace(/[^a-z0-9]+/gi, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal isOpen onClose={onClose} title={`Deelnemers – ${quiz.title}`} size="large">
      {error && <div className="admin-list-error">{error}</div>}
      {!data && !error && <div className="admin-list-loading"><div className="spinner"></div></div>}
      {data && (
        <div className="qp">
          <div className="qp-cards">
            <div className="qp-card"><strong>{data.summary.participants}</strong><span>Deelnemers</span></div>
            <div className="qp-card"><strong>{data.summary.averagePercentage}%</strong><span>Gemiddelde score</span></div>
            <div className="qp-card"><strong>{data.summary.perfectScores}</strong><span>Alles goed</span></div>
          </div>

          <div className="qp-toolbar">
            <div className="qp-tabs">
              <button type="button" className={tab === "participants" ? "on" : ""} onClick={() => setTab("participants")}>Deelnemers</button>
              <button type="button" className={tab === "questions" ? "on" : ""} onClick={() => setTab("questions")}>Per vraag</button>
            </div>
            {tab === "participants" && (
              <>
                <input className="qp-search" placeholder="Zoek op naam of e-mail" value={search} onChange={(e) => setSearch(e.target.value)} />
                {data.participants.length > 0 && (
                  <button type="button" className="btn btn-secondary" onClick={downloadCsv}>Exporteer CSV</button>
                )}
              </>
            )}
          </div>

          {tab === "participants" ? (
            <table className="admin-table">
              <thead>
                <tr><th>#</th><th>Naam</th><th>E-mail</th><th>Datum</th><th>Score</th><th></th></tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={6} className="no-data">Nog geen deelnames</td></tr>}
                {rows.map((p, i) => (
                  <tr key={`${p.participantId}-${i}`}>
                    <td>{data.participants.indexOf(p) + 1}</td>
                    <td>{p.name}</td>
                    <td>{p.email || "-"}</td>
                    <td>{fmtDate(p.participatedOn)}</td>
                    <td><span className={`qp-score ${tone(p.percentage)}`}>{p.score}/{p.total} ({p.percentage}%)</span></td>
                    <td><button type="button" className="btn btn-secondary" onClick={() => setSelected(p)}>Antwoorden</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            data.questions.map((q, i) => {
              const total = q.options.reduce((s, o) => s + o.count, 0);
              return (
                <div key={String(q._id)} className="qp-question">
                  <div className="qp-qhead">
                    <strong>{i + 1}. {q.questionText}</strong>
                    <span className={`qp-score ${tone(q.correctPercentage)}`}>{q.correctPercentage}% goed</span>
                  </div>
                  {q.options.map((o) => {
                    const share = total ? Math.round((o.count / total) * 100) : 0;
                    return (
                      <div key={String(o._id)} className="qp-option">
                        <span className={o.isCorrect ? "qp-correct" : ""}>{o.isCorrect ? "✓ " : ""}{o.text}</span>
                        <div className="qp-bar"><div style={{ width: `${share}%` }} className={o.isCorrect ? "ok" : ""} /></div>
                        <span className="qp-count">{o.count} ({share}%)</span>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}

          {selected && (
            <div className="qp-detail">
              <div className="qp-qhead">
                <strong>{selected.name} – {selected.score}/{selected.total} goed</strong>
                <button type="button" className="btn btn-secondary" onClick={() => setSelected(null)}>Sluiten</button>
              </div>
              {selected.answers.map((a, i) => {
                const q = questionById.get(a.questionId);
                const chosen = q?.options.find((o) => String(o._id) === a.selectedOptionId);
                const correct = q?.options.filter((o) => o.isCorrect).map((o) => o.text).join(", ");
                return (
                  <div key={`${a.questionId}-${i}`} className={`qp-answer ${a.isCorrect ? "right" : "wrong"}`}>
                    <div><strong>{i + 1}. {q?.questionText || "Vraag verwijderd"}</strong></div>
                    <div>Antwoord: {chosen?.text || "-"} {a.isCorrect ? "✓" : "✗"}</div>
                    {!a.isCorrect && correct && <div className="qp-correct">Juist: {correct}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
